import React, { FC, ReactNode }                                    from 'react';
import classNames                                                  from 'classnames';
import { FieldArray, useFormikContext }                            from "formik";
import _                                                           from "underscore";
import { FieldDescription, FieldWrapper, FormArrayErrorContainer } from "@src/views/components/forms";
import { MetaTagSearchInput }                                      from "@src/views/components/from-query/MetaTagSearchInput";
import { TagCloud }                                                from "@src/views/components/tag/TagCloud";
import { EmptyCollectionMessage }                                  from "@src/views/components/util/messaging/EmptyCollectionMessage";
import { FieldLabel }                                              from "@src/views/components/forms/formik/FieldLabel";
import { Cell, Grid }                                              from "styled-css-grid";


interface TagFieldProps {
    name: string,
    loading?: boolean,
    label?: string,
    descriptionText?: string,
    orientation?: 'horizontal' | 'vertical',
    reverseOrder?: boolean,
    containerClasses?: string | string[] | Record<string, any>,
    LabelComponent?: FC<{ content: ReactNode }>,
    DescriptionComponent?: FC<{ content: ReactNode }>
    EmptyComponent?: FC<{}>,
}

const TagFieldDefaultProps = {
    name:                 'tags',
    label:                'Meta Tags',
    descriptionText:      'Add tags to help categorize or further describe this item. To search for existing tags, start typing into the search and select any appropriate results.',
    containerClasses:     null,
    orientation:          'vertical',
    reverseOrder:         false,
    loading:              false,
    LabelComponent:       ( props ) => <FieldLabel>{ props.content }</FieldLabel>,
    DescriptionComponent: ( props ) => <FieldDescription>{ props.content }</FieldDescription>,
    EmptyComponent:       ( props ) => <EmptyCollectionMessage header={ 'No tags added' } content={ 'Search for tags, or add your own to see them here.' }/>,
} satisfies Partial<TagFieldProps>;

function TagField<T>( _props: TagFieldProps ){

    const props     = { ...TagFieldDefaultProps, ..._props },
          formik    = useFormikContext<T>(),
          fieldName = props.name;

    if( !fieldName ) return null;

    const { LabelComponent, DescriptionComponent, EmptyComponent } = props,
          isReverseOrder                                           = ( props.reverseOrder === true ),
          isHorizontal                                             = ( props.orientation === 'horizontal' ),
          tagValues                                                = formik.values[ fieldName ] ?? [],
          tagErrors                                                = formik.errors[ fieldName ];

    const handleAddedItem = ( value: string ) => {

        formik.setFieldValue( fieldName, _.uniq( [ ...tagValues, value ] ) );
    };

    return (

        <FieldWrapper>

            { props.label &&
              <LabelComponent content={ props.label }/> }

            { props.descriptionText &&
              <DescriptionComponent content={ props.descriptionText }/> }

            <FieldArray
                name={ fieldName }
                render={ arrayHelpers => {

                    let hasItems = !_.isEmpty( tagValues ),
                        labels   = hasItems ? tagValues : [];

                    return (
                        <Grid columns={ isHorizontal ? 2 : 1 }
                              areas={ isHorizontal
                                      ? isReverseOrder ? [ `input cloud` ] : [ `cloud input` ]
                                      : isReverseOrder ? [ `cloud`, `input` ] : [ `input`, `cloud` ] }
                              className={ classNames( props.containerClasses ) }>

                            <Cell area={ 'input' }>

                                <MetaTagSearchInput
                                    loading={ props.loading }
                                    allowAdditions={ true }
                                    onChange={ handleAddedItem }
                                    omit={ tagValues }/>

                            </Cell>
                            <Cell area={ 'cloud' }>

                                <FormArrayErrorContainer error={ tagErrors }>

                                    <TagCloud
                                        tags={ labels }
                                        onRemove={ ( index ) => arrayHelpers.remove( index ) }
                                        emptyComponent={ EmptyComponent }
                                    />

                                </FormArrayErrorContainer>

                            </Cell>

                        </Grid>
                    );
                } }
            />
        </FieldWrapper>

    );
}

export {
    TagField,
    TagFieldDefaultProps,
    type TagFieldProps,
};
